import { forkJoin as observableForkJoin, of, Subscription } from 'rxjs';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../common/utils/froala.utils';
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Router, NavigationStart, ActivatedRoute } from "@angular/router";

import { SubHeaderService } from "../../../core/services/sub-header.service";

import { SubHeader } from "../../../core/models/sub-header.model";
import { Issue, IssueType } from '../../../common/models/issue.model';
import { IssuesService, IssueSearchSpec } from '../../../common/services/issues.service';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { finalize, filter } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';

import { DatePipe } from '@angular/common';
import { DateService } from '../../../common/services/date.service';

@Component({
    selector: "issue-details",
    templateUrl: "./issue-details.component.html",
    styleUrls: ['./issue-details.component.scss'],
    standalone: false
})
export class IssueDetailsComponent implements OnInit {

    public _currentIssue = {} as Issue;
    //this is to preserve fields we don't expose via the form for now
    public _sourceIssue = {} as Issue;

    public issueDetailsForm: FormGroup;
    public subHeader: SubHeader = new SubHeader({
        title: "Manage Issue",
        buttons: [{ name: "Cancel", method: () => this.cancel() }, { name: "Save", method: () => this.save() }]
    });
    dataSubscriptions: Subscription = new Subscription();

    errorDialog: Dialog = new Dialog("An error has occurred during saving.  Please try again.  If the error persists, please contact support.", [new DialogButton("OK", true, () => this.onSaveFailDialogConfirm())]);
    successDialog: Dialog = new Dialog("The issue was saved successfully.", [new DialogButton("OK", true, () => this.onSaveSuccessDialogConfirm())]);

    froalaDescriptionEditor: any;
    froalaContestedReasonEditor: any;

    private _froalaDescriptionOptions: any = null;
    get froalaDescriptionOptions(): any {
        if (!this._froalaDescriptionOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaDescriptionOptions = applyFroalaAriaFix({
                heightMax: 300,
                heightMin: 150,
                key: key,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough'],
                events: {
                    'froalaEditor.initialized': (e, editor) => {
                        this.froalaDescriptionEditor = editor;
                    }
                }
            });
        }
        return this._froalaDescriptionOptions;
    };

    private _froalaContestedReasonOptions: any = null;
    get froalaContestedReasonOptions(): any {
        if (!this._froalaContestedReasonOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaContestedReasonOptions = applyFroalaAriaFix({
                heightMax: 300,
                heightMin: 150,
                key: key,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough'],
                events: {
                    'froalaEditor.initialized': (e, editor) => {
                        this.froalaContestedReasonEditor = editor;
                    }
                }
            });
        }
        return this._froalaContestedReasonOptions;
    };

	editableDatePickerOptions: any = { //IMyDpOptions = {
        dateFormat: DateService.DatePickerFormat,
        alignSelectorRight: true,
        indicateInvalidDate: true,
        inline: false,
    };

    public allIssueTypes: IssueType[];
    public creationDate: any;
    constructor(
        public readonly activatedRoute: ActivatedRoute,
        public readonly router: Router,
        public readonly formBuilder: FormBuilder,
        public subHeaderService: SubHeaderService,
        public dialogService: DynamicDialogService,
        public issuesService: IssuesService,
        public readonly authService: AuthService,
        public readonly datePipe: DatePipe
    ) { }

    ngOnInit(): void {

        this.subHeaderService.updateSubHeader(this.subHeader);

        this.dataSubscriptions.add(this.issuesService.getAllIssueTypes().subscribe(
            data => { if (data.length > 0) this.allIssueTypes = data; },
            error => { console.log(error); console.log("ERROR Fetching the Issue Types"); }
        ));

        const idParam = this.activatedRoute.snapshot.params["id"];
        let searchSpec = {
            ids: [idParam],
            loadChildren: true
        } as IssueSearchSpec;

        if (idParam) {
            this.dataSubscriptions.add(this.issuesService.getIssues(searchSpec).subscribe(
                data => { if (data.length > 0) this.issue = data[0]; this._sourceIssue = data[0];},
                error => { console.log(error); console.log("ERROR Fetching the Issue"); }
            ));
        }

        this.constructForm(this.issue);

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.dataSubscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        if (this.dataSubscriptions) {
            this.dataSubscriptions.unsubscribe();
        }
    }

    get issue(): Issue | null {
        if (this.issueDetailsForm && this.issueDetailsForm.valid)
            this._currentIssue = this._createIssue();
        return this._currentIssue;
    }

    set issue(issue: Issue) {
        this._currentIssue = issue;
        this.constructForm(issue);
    };

    public _createIssue(): Issue {
        if (!this.issueDetailsForm || !this.issueDetailsForm.valid)
            return this._currentIssue;

        //preserving fields not in form
        let issue = JSON.parse(JSON.stringify(this._sourceIssue)) as Issue;

        let controls = this.issueDetailsForm.controls;

        issue.creationDate = controls.creationDate.get('datePicker')?.value;
        issue.contestedReason = controls.contestedReason.get('datePicker')?.value;
        issue.description = stripFroalaWatermark(controls.description.value);
        issue.dateDue = controls.dateDue.get('datePicker')?.value;
        issue.holdId = controls.holdId.value;
        issue.issueOwner = controls.issueOwner.value;
        issue.inspectorId = controls.inspectorId.value;
        issue.mostRecentInspectionDate = controls.mostRecentInspectionDate.get('datePicker')?.value;
        issue.legacyId = controls.legacyId.value;
        issue.organizationId = controls.organizationId.value;
        issue.organizationNameAtTimeOfIssue = controls.organizationNameAtTimeOfIssue.value;
        issue.resolutionDate = controls.resolutionDate.get('datePicker')?.value;
        issue.typeId = controls.typeId.value;
        issue.wasIssueContested = controls.wasIssueContested.value;

        //If any of the chief's order related properties were updated, persist them
        if (!this.objectIsNullOrUndefined(controls.chiefsOrderValidated.value) || !this.objectIsNullOrUndefined(controls.chiefsOrderDueDate.value)
            || !this.objectIsNullOrUndefined(controls.chiefsOrderCreationDate.value) || !this.objectIsNullOrUndefined(controls.chiefsOrderSignDate.value)
            || !this.objectIsNullOrUndefined(controls.chiefsOrderSentDate.value))
        {
            issue.issueChiefsOrderTracking.chiefsOrderCreationDate = controls.chiefsOrderCreationDate.get('datePicker')?.value;
            issue.issueChiefsOrderTracking.chiefsOrderDueDate = controls.chiefsOrderDueDate.get('datePicker')?.value;
            issue.issueChiefsOrderTracking.chiefsOrderSentDate = controls.chiefsOrderSentDate.get('datePicker')?.value;
            issue.issueChiefsOrderTracking.chiefsOrderSignDate = controls.chiefsOrderSignDate.get('datePicker')?.value;
            issue.issueChiefsOrderTracking.wasChiefsOrderValidated = controls.chiefsOrderValidated.value;
        }


        return Object.assign(this._currentIssue, issue);
    }

    public objectIsNullOrUndefined(objectToCheck: any): boolean {
        return objectToCheck == null || objectToCheck == undefined;
    }

    public cancel(): void {
        this.router.navigate(["/dashboard/view/ricsDashboard"]);
    }

    public chiefsOrderExists(): boolean {
        return !this.objectIsNullOrUndefined(this._currentIssue.chiefsOrderId);
    }

    public contestedToggle(isSelected: boolean): void {
        if (isSelected)
            this.froalaContestedReasonEditor.edit.on();
        else
            this.froalaContestedReasonEditor.edit.off();
    }

    //Need to do it this way to trigger change detection
	setDatePickerDisabled(optionsToToggle: any, disabled: boolean): any {//: IMyDpOptions, disabled: boolean): IMyDpOptions {
        let optionsToToggleCopy = JSON.parse(JSON.stringify(optionsToToggle));

        if (this.isReadOnly())
            optionsToToggleCopy.componentDisabled = true
        else
            optionsToToggleCopy.componentDisabled = disabled;

        return optionsToToggleCopy;
    }

    public constructForm(issue: Issue): void {
        let isReadOnly = this.isReadOnly();
        if (isReadOnly && this.froalaDescriptionEditor)
            this.froalaDescriptionEditor.edit.off();

        if ((isReadOnly || !issue.wasIssueContested) && this.froalaContestedReasonEditor)
            this.froalaContestedReasonEditor.edit.off();


        let wasChiefsOrderValidated = !this.objectIsNullOrUndefined(issue.issueChiefsOrderTracking) ? issue.issueChiefsOrderTracking.wasChiefsOrderValidated : null;

        // let chiefsOrderDueDate = !this.objectIsNullOrUndefined(issue.issueChiefsOrderTracking) ? this.convertDateForDatePicker(issue.issueChiefsOrderTracking.chiefsOrderDueDate) : null;
        // let chiefsOrderCreationDate = !this.objectIsNullOrUndefined(issue.issueChiefsOrderTracking) ? this.convertDateForDatePicker(issue.issueChiefsOrderTracking.chiefsOrderCreationDate) : null;
        // let chiefsOrderSignDate = !this.objectIsNullOrUndefined(issue.issueChiefsOrderTracking) ? this.convertDateForDatePicker(issue.issueChiefsOrderTracking.chiefsOrderSignDate) : null;
        // let chiefsOrderSentDate = !this.objectIsNullOrUndefined(issue.issueChiefsOrderTracking) ? this.convertDateForDatePicker(issue.issueChiefsOrderTracking.chiefsOrderSentDate) : null;

        this.issueDetailsForm = this.formBuilder.group({
            legacyId: [issue.legacyId],
            organizationId: [issue.organizationId, [Validators.required]],
            organizationNameAtTimeOfIssue: [issue.organizationNameAtTimeOfIssue],
            description: [issue.description, [Validators.required]],
            inspectorId: [issue.inspectorId],
            creationDate: this.formBuilder.group({ datePicker: [{ value: issue.creationDate, disabled: this.isReadOnly() }, [Validators.required]]}),
            dateDue: this.formBuilder.group({ datePicker: {value: issue.dateDue, disabled: this.isReadOnly() } }),
            resolutionDate: this.formBuilder.group( { datePicker: [{ value: issue.resolutionDate, disabled: this.isReadOnly() }]}),
            mostRecentInspectionDate: this.formBuilder.group( { datePicker: [{value: issue.mostRecentInspectionDate, disabled: this.isReadOnly()}]}),
            typeId: [issue.typeId, [Validators.required]],
            issueOwner: [issue.issueOwner, [Validators.required]],
            wellId: [issue.wellId],
            wellNameAtTimeOfIssue: [issue.wellNameAtTimeOfIssue],
            holdId: [issue.holdId],
            wasIssueContested: [issue.wasIssueContested],
            contestedReason: [issue.contestedReason],
            letterReady: [issue.letterReady],
            certificationNumber1stLetter: [issue.certificationNumber1stLetter],
            certificationNumber2ndLetter: [issue.certificationNumber2ndLetter],
            chiefsOrderId: [issue.chiefsOrderId],
            chiefsOrderValidated: [wasChiefsOrderValidated],
            chiefsOrderDueDate: this.formBuilder.group({ value: issue?.issueChiefsOrderTracking?.chiefsOrderDueDate, disabled: this.isReadOnly() }),
            chiefsOrderCreationDate: this.formBuilder.group({ value: issue?.issueChiefsOrderTracking?.chiefsOrderCreationDate, disabled: this.isReadOnly() }),
            chiefsOrderSignDate: this.formBuilder.group({ value: issue?.issueChiefsOrderTracking?.chiefsOrderSignDate, disabled: this.isReadOnly() }),
            chiefsOrderSentDate: this.formBuilder.group({ value: issue?.issueChiefsOrderTracking?.chiefsOrderSentDate, disabled: this.isReadOnly()})
        });

        //force form validation
        this.forceValidation();
    }

    public isReadOnly(): boolean {
        let resolved = false;
        if (this.issue?.resolutionDate)
            resolved = true;
        return !this.authService.User.isInternal || resolved;
    }

    public save() {
        let issue = this.issue;
        if (this.issueDetailsForm.valid && issue != null ) {
            this.issuesService.saveIssue(issue).subscribe(data => {
                this.issue = data;
                this.successDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.successDialog);
            }, error => {
                this.errorDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.errorDialog);
            });
        } else {
            //force form validation
            this.forceValidation();
        }
    }

    forceValidation() {
        Object.keys(this.issueDetailsForm.controls).forEach(field => {
            const control = this.issueDetailsForm.get(field);
            control.markAsTouched({ onlySelf: true });
        });
    }

    public onSaveFailDialogConfirm(): void {
        //Do nothing
    }

    public onSaveSuccessDialogConfirm(): void {
        this.router.navigate(["/dashboard/view/ricsDashboard"]);
    }

    public onInspectorIdBlur(event: any): void {
        this.checkInspectionDateDisabledStatus();
    }

	public checkInspectionDateDisabledStatus(): void {
        let inspectionDatePickerDisabled = false;
        if (this.objectIsNullOrUndefined(this.issue?.inspectorId) || this.issue?.inspectorId == "") inspectionDatePickerDisabled = true;
    }
}
